/**
 * OPRA trade-condition codes, for the ones that matter when reading a tape.
 *
 * The exchange already states whether a print was one leg of a package — there is no need to infer
 * it from size or timing. These are the codes the backend decodes in `theta_trade_conditions.py`;
 * only the multi-leg family is mirrored here, since that is what the tape needs to tag.
 */

/** Option-only packages: verticals, calendars, condors, risk reversals. */
const MULTI_LEG_OPTION_CODES: Record<number, string> = {
  130: 'Multi-leg auto-electronic',
  131: 'Multi-leg auction',
  132: 'Multi-leg cross',
  133: 'Multi-leg floor',
  134: 'Multi-leg vs single leg',
  136: 'Multi-leg auction vs single leg',
  137: 'Multi-leg floor vs single leg',
  144: 'Multi-leg floor, price prot.',
  147: 'Multilateral compression',
};

/** Packages that include the underlying stock (buy-writes, delta-hedged prints). */
const MULTI_LEG_STOCK_CODES: Record<number, string> = {
  135: 'Stock+options auction',
  138: 'Stock+options auto-electronic',
  139: 'Stock+options cross',
  140: 'Stock+options floor',
  141: 'Stock+options vs single leg',
  142: 'Stock+options auction vs single leg',
  143: 'Stock+options floor vs single leg',
};

export type MultiLegKind = 'options' | 'with-stock';

export interface MultiLegInfo {
  kind: MultiLegKind;
  /** Human label for the specific condition, e.g. "Multi-leg auction". */
  label: string;
  code: number;
}

/** Every condition code carried by a row: the primary plus any extension codes. */
function codesOf(row: {
  condition?: number | null;
  condition_codes?: number[] | null;
}): number[] {
  const out: number[] = [];
  if (typeof row.condition === 'number') out.push(row.condition);
  for (const c of row.condition_codes ?? []) {
    if (typeof c === 'number' && !out.includes(c)) out.push(c);
  }
  return out;
}

/**
 * The multi-leg nature of a print, or null when the exchange did not flag one.
 * Option-only packages win over stock combos when a row somehow carries both.
 */
export function multiLegInfo(row: {
  condition?: number | null;
  condition_codes?: number[] | null;
}): MultiLegInfo | null {
  const codes = codesOf(row);
  for (const c of codes) {
    const label = MULTI_LEG_OPTION_CODES[c];
    if (label) return { kind: 'options', label, code: c };
  }
  for (const c of codes) {
    const label = MULTI_LEG_STOCK_CODES[c];
    if (label) return { kind: 'with-stock', label, code: c };
  }
  return null;
}

export function isMultiLegPrint(row: {
  condition?: number | null;
  condition_codes?: number[] | null;
}): boolean {
  return multiLegInfo(row) !== null;
}
